import React from 'react';
import { ServedFace } from './ServedFace';
import { CopyIcon, PencilStampIcon, ReceiptIcon, RetryIcon } from './icons';
import { pillFace } from './SecondaryAxisPills';
import { SplitPill, SPLIT_PILL_HEIGHT, splitPillIconSize, type SplitPillVariant } from './SplitPill';
import { InfoTooltip } from './InfoTooltip';
import { theme } from '../theme';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';

// <purpose>THE one renderer of the four clerk traces — the retry count, Kopiert, Bearbeitet,
// Eingang bestätigt — as SPLIT PILLS with the act's icon as the key (small facts on a row,
// operator 2026-10-19, round 3): „↻ | 1×" on row 2 beside the classification, the other three on
// row 3 right ahead of the decision badge. Before this (part 7, 2026-10-11) they were muted plain
// text, which turned the retry count badge into a sentence on a row that cannot hold one.
// Registry: design/design-languages.html, entry `bearbeitung_traces`.</purpose>
// <constraints>
//   - The testids are unchanged from the text traces and the badges before them (`kopiert-badge`,
//     ...): the registry parity test and every row test key on them.
//   - Served words: kopiert -> decision.copy, eingang_bestaetigt -> decision.receipt (ServedFace
//     owns the shimmer / served-null / raw-key floor). „Bearbeitet" has no served face. The count
//     reads „n×" — the only face this file composes.
//   - Hover: Kopiert explains itself („Antwort kopiert, nicht förmlich genehmigt" — it only stands
//     on a letter not approved, see settledRow.showsKopiert); the receipt
//     hovers the copy toast's own sentence, passed by the caller; the count and Bearbeitet hover
//     nothing (nothing to add).
//   - `testId` overrides the default id for a surface that shows several traces of one kind at
//     once (the draft cards: one per slot); the ticket-level sites keep the defaults.
//   - Visibility (the clerk's „Hinweise in der Liste") is the CALLER's job, via the registry id.
// </constraints>
export type ClerkTraceKind = 'reanalysis' | 'kopiert' | 'eingang_bestaetigt' | 'bearbeitet';

const TESTID: Record<ClerkTraceKind, string> = {
  reanalysis: 'reanalysis-count-badge',
  kopiert: 'kopiert-badge',
  eingang_bestaetigt: 'eingang-bestaetigt-badge',
  bearbeitet: 'bearbeitet-badge',
};

export const KOPIERT_HOVER = 'Antwort kopiert, nicht förmlich genehmigt';

// The served-word pills' footprint while the vocabulary loads: a short word at the pill's metrics.
const SKELETON_WIDTH: Partial<Record<ClerkTraceKind, number>> = { kopiert: 72, eingang_bestaetigt: 104 };

// Resolved at RENDER, never at module load: should an import cycle ever form between this file and
// its icons, a module-level table would capture `undefined` for whichever side loads second.
// Every trace is a FACT, so every key is a stamp (icon set, 2026-10-31): the retry ring's stamp, not
// Neu analysieren's outline; the pencil's stamp, not Bearbeiten's.
const RetryStamp: React.FC<{ size?: number; color?: string }> = p => <RetryIcon {...p} stamp />;
const iconFor = (kind: ClerkTraceKind): React.FC<{ size?: number; color?: string }> => ({
  reanalysis: RetryStamp,
  kopiert: CopyIcon,
  eingang_bestaetigt: ReceiptIcon,
  bearbeitet: PencilStampIcon,
})[kind];

export function ClerkTrace({ kind, count, title, testId, variant = 'row' }: {
  kind: ClerkTraceKind; count?: number; title?: string; testId?: string; variant?: SplitPillVariant;
}): React.ReactElement | null {
  const Icon = iconFor(kind);
  const draw = (text: string, tip?: string | null) => {
    const pill = (
      <SplitPill
        testId={testId ?? TESTID[kind]}
        variant={variant}
        icon={<Icon size={splitPillIconSize(variant)} />}
        style={{ animation: 'stx-fadeIn 0.3s ease' }}
      >
        {text}
      </SplitPill>
    );
    return tip ? <InfoTooltip text={tip}>{pill}</InfoTooltip> : pill;
  };
  switch (kind) {
    case 'reanalysis':
      return draw(`${count ?? 0}×`, title);
    case 'kopiert':
      return (
        <ServedFace family="decision" faceKey="copy" width={SKELETON_WIDTH.kopiert} height={SPLIT_PILL_HEIGHT[variant]}
          render={word => draw(pillFace('info', word), title ?? KOPIERT_HOVER)} />
      );
    case 'eingang_bestaetigt':
      return (
        <ServedFace family="decision" faceKey="receipt" width={SKELETON_WIDTH.eingang_bestaetigt} height={SPLIT_PILL_HEIGHT[variant]}
          render={word => draw(pillFace('info', word), title)} />
      );
    case 'bearbeitet':
      return draw(pillFace('info', 'bearbeitet'), title);
  }
}

// <purpose>The traces' SECOND face (detail pane, 2026-10-31): muted 11 px words on the letter's
// heading line — „Kopiert · Bearbeitet · Eingang bestätigt" — right of the title (and WIM's letter
// switch), left of „Vergleichen". The list row keeps the split pills above. The retry count is not
// a text trace: on the pane it stands beside Klassifikationskonfidenz as its qualifier (header
// line 4, the pill face). Registry: entry `bearbeitung_traces`, the trace text face.</purpose>
// <constraints>Same served words as the pills (decision.copy, decision.receipt; „Bearbeitet" has no
// served face), same hovers. Each word keeps its kind's testid so one id finds a trace on every
// surface. The caller gives the line a flex:1 slot, so a word landing moves nothing beside it.</constraints>
export function ClerkTraceText({ kinds, receiptTitle }: {
  kinds: readonly Exclude<ClerkTraceKind, 'reanalysis'>[]; receiptTitle?: string;
}): React.ReactElement {
  const cm = useCategoryMeta();
  const wordOf = (kind: Exclude<ClerkTraceKind, 'reanalysis'>): string | null => {
    if (kind === 'bearbeitet') return pillFace('info', 'bearbeitet');
    if (cm.configLoading) return null;
    const served = cm.face('decision', kind === 'kopiert' ? 'copy' : 'receipt');
    return served === null ? null : pillFace('info', served);
  };
  const hoverOf = (kind: Exclude<ClerkTraceKind, 'reanalysis'>): string | undefined =>
    kind === 'kopiert' ? KOPIERT_HOVER : kind === 'eingang_bestaetigt' ? receiptTitle : undefined;
  const words = kinds.map(kind => ({ kind, word: wordOf(kind) })).filter(w => w.word !== null);
  return (
    <span data-testid="trace-text" style={{
      flex: '1 1 0', minWidth: 0, textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      fontSize: 11, color: theme.colors.navyMuted,
    }}>
      {words.map(({ kind, word }, i) => {
        const hover = hoverOf(kind);
        const face = (
          <span key={kind} data-testid={TESTID[kind]}
            style={{ animation: 'stx-fadeIn 0.3s ease', outline: 'none' }}>{word}</span>
        );
        return (
          <React.Fragment key={kind}>
            {i > 0 && ' · '}
            {hover ? <InfoTooltip text={hover}>{face}</InfoTooltip> : face}
          </React.Fragment>
        );
      })}
    </span>
  );
}
